import React, { useState } from "react";

import BadgesContainer from "../BadgesContainer";
import { isoToDateTime } from "../date.functions";
import KeyDecisionInfo from "../key-decision-info/KeyDecisionInfo";
import SecondaryInfo from "../SecondaryInfo";
import VehicleResultCardDesktop from "./VehicleResultCardDesktop";
import VehicleResultCardMobile from "./VehicleResultCardMobile";
import VehicleResultCardTablet from "./VehicleResultCardTablet";
import { CardsWrapperProps } from "./VehicleResultCard.type";
import PricingAndVAT from "../pricing-info/PricingAndVAT";
import TrackingWrapper from "../tracking/TrackingWrapper";
import VehicleDetailsAnalytics from "../analytics/VehicleDetailsAnalytics.type";
import { MappedCurrentBid } from "../vehicle-search-details/currentBids.type";
import { ViewportSizeType } from "../hooks/useViewport.hook";
import { MechanicalReportAnalytics } from "../analytics/MechanicalReportAnalytics";
import getBatteryGradeLetter from "../utils/getBatteryGradeLetter";
import { Items } from "../vehicle-search-details/vehicleSearch.type";
import { itemCleanedLink } from "./components/LinkToDetailsPage";
import getCMSDomain from "./getCMSDomain";
import getEnvironment from "../getEnvironment";

type BadgeVariantType = "Default" | "Update" | "Negative" | "Alert";

const VehicleResultCard: React.FunctionComponent<{
  vehicleInfo: Items;
  userType: string;
  viewportWidth: ViewportSizeType;
  selectedVehicles: string[];
  setSelectedVehicles: React.Dispatch<React.SetStateAction<any[]>>;
  currentBids: MappedCurrentBid[] | null;
  showFilters?: boolean;
  pdfEnabled?: boolean;
}> = ({
  vehicleInfo,
  userType,
  viewportWidth,
  selectedVehicles,
  setSelectedVehicles,
  currentBids,
  showFilters,
  pdfEnabled,
}) => {
  const [isVehicleTracked, setIsVehicleTracked] = useState(
    vehicleInfo?.isTracked
  );

  const mechanicalReport = vehicleInfo?.reportUrls?.find(
    (report) => report.rel === "mechanicalreporturl"
  );

  const mechanicalReportAnalyticsData = MechanicalReportAnalytics({
    reportType: mechanicalReport?.type,
    categoryMapping: vehicleInfo?.assessment?.categoryMapping,
  });

  const validNumberOfOwners =
    Boolean(vehicleInfo.numberOfOwners) &&
    vehicleInfo.numberOfOwners !== "" &&
    vehicleInfo.numberOfOwners !== "-";
  const hasMoreThanOneOwner = vehicleInfo?.numberOfOwners > "1";
  const ownerTextToDisplay = hasMoreThanOneOwner
    ? `${vehicleInfo?.numberOfOwners} Owners`
    : `${vehicleInfo?.numberOfOwners} Owner`;

  const vehicleAnalytics: VehicleDetailsAnalytics = {
    batteryGrade: vehicleInfo.additionalInformation?.evBattery?.isApplicable
      ? `grade-${getBatteryGradeLetter(
          vehicleInfo.additionalInformation?.evBattery?.batteryCondition
        )}`
      : "",
    vrm: vehicleInfo?.vrm || "",
    vehicleType: vehicleInfo?.vehicleType || "",
    unitPrice: vehicleInfo.buyNowPrice
      ? vehicleInfo.buyNowPrice.replace("£", "").replace(",", "")
      : "",
    saleCode: vehicleInfo?.saleCode || "",
    lotCode: vehicleInfo?.lotNumber || "",
    saleType: vehicleInfo?.saleType || "",
    saleBranch: vehicleInfo?.localSaleLocation || "",
    recentlyAddedFlag: vehicleInfo.isRecentlyAdded ? "Yes" : "No",
    mechReportCombo: mechanicalReportAnalyticsData
      ? mechanicalReportAnalyticsData.toString()
      : null,
    hpiSpecFlag: vehicleInfo?.hpi ? "hpi-available" : "hpi-is-not-available",
  };

  const mileageWarrantedText = {
    Yes: "Warranted",
    Warranted: "Warranted",
    "Not warranted": "Not warranted",
    No: "Not warranted",
    Incorrect: "Incorrect mileage",
    Unknown: "Unknown",
  };

  const getCurrentBid = () => {
    return currentBids?.find((bid) => bid.vrm === vehicleInfo.vrm)?.currentBid;
  };

  const getImages = (images) => {
    return images.map((image) => {
      return { url: image.imageURI, alt: "" };
    });
  };

  const CMSdomainURL = getCMSDomain(getEnvironment());

  const HPISpecCheckLink = `${itemCleanedLink(
    CMSdomainURL,
    vehicleInfo.vrm,
    true
  )}`;

  const mileageForSecondaryInfo = `${vehicleInfo?.mileage.toLocaleString()} miles (${
    mileageWarrantedText[vehicleInfo?.mileageWarranted]
  })`;
  return (
    <Wrapper
      showFilters={showFilters}
      userType={userType}
      exportId={pdfEnabled ? vehicleInfo.vrm : vehicleInfo.vehicleId}
      key={vehicleInfo.vrm}
      lotNumber={vehicleInfo?.lotNumber}
      vrmNumber={vehicleInfo.vrm}
      primaryDescription={vehicleInfo?.primaryVehicleDescription}
      selectedVehicles={selectedVehicles}
      setSelectedVehicles={setSelectedVehicles}
      images={getImages(vehicleInfo?.images)}
      keyDecisionInfoSlot={
        <KeyDecisionInfo
          owners={vehicleInfo?.numberOfOwners}
          viewport={viewportWidth}
          grade={vehicleInfo?.grade}
          vehicleInformation={vehicleInfo}
          batteryReport={vehicleInfo?.additionalInformation?.evBattery}
          batteryReportLink={vehicleInfo?.reportUrls?.find(
            (report) => report.rel === "evbatteryreporturl"
          )}
          conditionReport={vehicleInfo?.reportUrls?.find(
            (report) => report.rel === "conditionreporturl"
          )}
          mechanicalReport={vehicleInfo?.reportUrls?.find(
            (report) => report.rel === "mechanicalreporturl"
          )}
          vehicleAnalytics={vehicleAnalytics}
          categoryMapping={vehicleInfo?.assessment?.categoryMapping}
        />
      }
      saleInfoSlot={
        <SecondaryInfo
          listOfItems={
            vehicleInfo?.saleChannel !== "InternetAuction"
              ? [
                  isoToDateTime(vehicleInfo?.saleDate),
                  vehicleInfo?.localSaleLocation,
                  vehicleInfo?.saleType,
                ]
              : [
                  isoToDateTime(vehicleInfo?.saleDate),
                  vehicleInfo?.localSaleLocation,
                  vehicleInfo?.publishedSaleType,
                  vehicleInfo?.localSaleType,
                ]
          }
        ></SecondaryInfo>
      }
      secondaryInfoSlot={
        <SecondaryInfo
          listOfItems={[
            mileageForSecondaryInfo,
            vehicleInfo?.plate,
            validNumberOfOwners ? ownerTextToDisplay : null,
            vehicleInfo?.manufacturerColour,
            vehicleInfo?.fuelType,
            vehicleInfo?.transmission,
            vehicleInfo?.numberOfDoors + " doors",
            "V5 " + vehicleInfo?.logBookStatusIndicator,
            vehicleInfo?.serviceHistoryPresent
              ? "Service History"
              : "No Service History",
          ]}
        />
      }
      printUrls={vehicleInfo?.searchCardPrintUrls}
      userNote={vehicleInfo?.userNote}
      badgesSlot={
        <BadgesContainer
          badgeList={[
            {
              label: vehicleInfo.isRecentlyAdded ? "Recently Added" : null,
              variant: "Update" as BadgeVariantType,
            },
          ].concat(
            vehicleInfo.saleChannelTags.map((tag: string) => ({
              label: tag,
              variant: "Default" as BadgeVariantType,
            }))
          )}
        ></BadgesContainer>
      }
      recentlyAddedBadge={vehicleInfo.isRecentlyAdded ? "Recently Added" : null}
      pricingInfoSlot={
        userType !== "Viewer" && (
          <PricingAndVAT
            saleChannel={vehicleInfo?.saleChannel}
            saleChannelTags={vehicleInfo?.saleChannelTags}
            buyNowPrice={vehicleInfo?.buyNowPrice}
            capClean={vehicleInfo?.capClean}
            vatType={vehicleInfo?.vatType}
            currentBid={getCurrentBid()}
            hpi={vehicleInfo?.hpi}
            HPISpecCheckLink={HPISpecCheckLink}
            vehicleAnalytics={vehicleAnalytics}
          />
        )
      }
      isTracked={isVehicleTracked}
      trackBtnSlot={
        <TrackingWrapper
          saleCode={vehicleInfo?.saleCode}
          vrm={vehicleInfo.vrm}
          isVehicleTracked={isVehicleTracked}
          setIsVehicleTracked={setIsVehicleTracked}
          vehicleAnalytics={vehicleAnalytics}
          inventoryID={vehicleInfo.inventoryData.inventoryID}
        />
      }
      vehicleAnalytics={vehicleAnalytics}
      viewportWidth={viewportWidth}
      pdfEnabled={pdfEnabled}
      imageCarouselIndexes={vehicleInfo?.imageCarouselIndexes}
    />
  );
};

const Wrapper: React.FunctionComponent<CardsWrapperProps> = ({
  images,
  exportId,
  vrmNumber,
  lotNumber,
  primaryDescription,
  keyDecisionInfoSlot,
  badgesSlot,
  recentlyAddedBadge,
  saleInfoSlot,
  secondaryInfoSlot,
  trackBtnSlot,
  pricingInfoSlot,
  printUrls,
  userNote,
  isTracked,
  vehicleAnalytics,
  viewportWidth,
  selectedVehicles,
  setSelectedVehicles,
  allQueryStrings,
  showFilters,
  pdfEnabled,
  imageCarouselIndexes
}) => {
  return (
    <>
      {(viewportWidth === "desktop" || viewportWidth === "smallDesktop") && (
        <VehicleResultCardDesktop
          allQueryStrings={allQueryStrings}
          images={images}
          exportId={exportId}
          vrmNumber={vrmNumber}
          lotNumber={lotNumber}
          primaryDescription={primaryDescription}
          keyDecisionInfoSlot={keyDecisionInfoSlot}
          recentlyAddedBadge={recentlyAddedBadge}
          saleInfoSlot={saleInfoSlot}
          secondaryInfoSlot={secondaryInfoSlot}
          trackBtnSlot={trackBtnSlot}
          pricingInfoSlot={pricingInfoSlot}
          printUrls={printUrls}
          userNote={userNote}
          isTracked={isTracked}
          vehicleAnalytics={vehicleAnalytics}
          selectedVehicles={selectedVehicles}
          setSelectedVehicles={setSelectedVehicles}
          showFilters={showFilters}
          viewportWidth={viewportWidth}
          pdfEnabled={pdfEnabled}
          imageCarouselIndexes={imageCarouselIndexes}
        />
      )}
      {viewportWidth === "tablet" && (
        <VehicleResultCardTablet
          images={images}
          exportId={exportId}
          vrmNumber={vrmNumber}
          lotNumber={lotNumber}
          primaryDescription={primaryDescription}
          keyDecisionInfoSlot={keyDecisionInfoSlot}
          badgesSlot={badgesSlot}
          saleInfoSlot={saleInfoSlot}
          secondaryInfoSlot={secondaryInfoSlot}
          trackBtnSlot={trackBtnSlot}
          pricingInfoSlot={pricingInfoSlot}
          printUrls={printUrls}
          userNote={userNote}
          isTracked={isTracked}
          vehicleAnalytics={vehicleAnalytics}
          selectedVehicles={selectedVehicles}
          setSelectedVehicles={setSelectedVehicles}
          pdfEnabled={pdfEnabled}
          imageCarouselIndexes={imageCarouselIndexes}
        />
      )}
      {viewportWidth === "mobile" && (
        <VehicleResultCardMobile
          images={images}
          exportId={exportId}
          vrmNumber={vrmNumber}
          lotNumber={lotNumber}
          primaryDescription={primaryDescription}
          keyDecisionInfoSlot={keyDecisionInfoSlot}
          badgesSlot={badgesSlot}
          saleInfoSlot={saleInfoSlot}
          secondaryInfoSlot={secondaryInfoSlot}
          trackBtnSlot={trackBtnSlot}
          pricingInfoSlot={pricingInfoSlot}
          printUrls={printUrls}
          userNote={userNote}
          isTracked={isTracked}
          vehicleAnalytics={vehicleAnalytics}
          selectedVehicles={selectedVehicles}
          setSelectedVehicles={setSelectedVehicles}
          pdfEnabled={pdfEnabled}
          viewportWidth={viewportWidth}
          imageCarouselIndexes={imageCarouselIndexes}
        />
      )}
    </>
  );
};

export default VehicleResultCard;
